/**
 * Service worker registration + update handling.
 *
 * Update strategy (see PWA_UPDATE_STRATEGY.md):
 *  - Register on load. The SW pre-caches the app shell + prototype assets.
 *  - When a new SW is found and finishes installing, we DO NOT force a reload
 *    mid-session (a child may be drawing). The new version activates on the next
 *    natural launch. `onUpdateReady` is exposed for a future gentle prompt.
 *  - HTML and the SW file itself are network-first / no-store at the edge, so a
 *    stale SW can always be replaced (no permanent lock-in).
 */
export interface SwHandle {
  onUpdateReady(fn: () => void): void;
  onActivated(fn: () => void): void;
}

export function registerServiceWorker(): SwHandle {
  const updateReady = new Set<() => void>();
  const activated = new Set<() => void>();
  const handle: SwHandle = {
    onUpdateReady: (fn) => updateReady.add(fn),
    onActivated: (fn) => activated.add(fn),
  };

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return handle;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('./sw.js', { scope: './' })
      .then((reg) => {
        // A worker is waiting already (update found on a previous load).
        if (reg.waiting && navigator.serviceWorker.controller) {
          updateReady.forEach((fn) => fn());
        }
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // New content available; will apply on next launch.
              updateReady.forEach((fn) => fn());
            }
            if (installing.state === 'activated') {
              activated.forEach((fn) => fn());
            }
          });
        });
      })
      .catch((err) => {
        console.warn('[pwa] service worker registration failed:', err);
      });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      activated.forEach((fn) => fn());
    });
  });

  return handle;
}
